import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Skeleton,
  Divider,
  Grid,
  Tooltip,
  Stack,
  MenuItem,
  Autocomplete,
  useTheme,
} from '@mui/material';
import {
  Visibility,
  Search,
  Refresh,
  FilterList,
  FilterListOff,
  AccessTime,
  Computer,
  CloudUpload,
  CheckCircle,
  ErrorOutline,
  HourglassEmpty,
  Schedule,
  Inbox,
  AttachFile as FileIcon,
  Send as SendIcon,
  Close as CloseIcon,
  ReceiptLong,
  Insights,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import authService from '../shared/services/authService';
import PageTitleBar from './PageTitleBar';
import StatCard from './admin/StatCard';
import FileViewerModal from './FileViewerModal';
import { useAuthStore } from '../store/authStore';
import { useTranslation } from '../store/languageStore';
import { brand } from '../theme';

interface ProcessingHistoryItem {
  id: string;
  file_id?: string;
  module_name: string;
  module_description: string;
  file_name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  client_ip: string;
  user_agent: string;
  transaction_type: string;
  status: 'success' | 'failed' | 'processing' | 'pending';
  error_message?: string;
  processing_time?: string;
  data_identifier?: string;
  extracted_data: any;
  posted_data: any;
  api_response: any;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  retry_count: number;
}

interface DetailedTransaction {
  id: string;
  status: string;
  transaction_type: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  processing_time?: string;
  retry_count: number;
  error_message?: string;
  client_ip: string;
  user_agent: string;
  module: {
    id: string;
    name: string;
    description: string;
    api_endpoint: string;
    auth_type: string;
  };
  file: {
    id: string;
    original_filename: string;
    file_path: string;
    file_size: number;
    mime_type: string;
    uploaded_at: string;
  };
  extracted_data: any;
  posted_data: any;
  api_response: any;
}

interface ActivityStats {
  total_documents: number;
  document_processing: number;
  tariff_classification: number;
  email_processing: number;
}

// Maps a transaction status to its semantic MUI colour + icon, reused by the
// status pill in the table, the expanded row and the detail dialog.
const STATUS_META: Record<
  string,
  { color: 'success' | 'error' | 'warning' | 'info' | 'default'; icon: React.ReactElement }
> = {
  success: { color: 'success', icon: <CheckCircle sx={{ fontSize: 16 }} /> },
  completed: { color: 'success', icon: <CheckCircle sx={{ fontSize: 16 }} /> },
  failed: { color: 'error', icon: <ErrorOutline sx={{ fontSize: 16 }} /> },
  processing: { color: 'warning', icon: <HourglassEmpty sx={{ fontSize: 16 }} /> },
  pending: { color: 'info', icon: <Schedule sx={{ fontSize: 16 }} /> },
};

// Humanises a status not covered by a translation key (e.g. "completed").
const humanizeStatus = (status: string) =>
  status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';

const ProcessingHistory: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { tString } = useTranslation();
  const { user } = useAuthStore();
  const isAdmin = user?.is_admin || false;

  const [history, setHistory] = useState<ProcessingHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState<ActivityStats | null>(null);
  const [detailDialog, setDetailDialog] = useState<{
    open: boolean;
    transaction?: DetailedTransaction;
  }>({ open: false });
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [selectedFileTransaction, setSelectedFileTransaction] = useState<ProcessingHistoryItem | null>(null);

  // Admin filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filterUserId, setFilterUserId] = useState<string>('');
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [usersList, setUsersList] = useState<any[]>([]);

  const hasActiveFilters = Boolean(filterUserId || filterDateFrom || filterDateTo || filterStatus);

  // Create memoized options array with "All Users" at the top
  const userFilterOptions = useMemo(() => {
    return [{ id: '', name: tString('adminPage.processingHistory.filters.allUsers'), email: '' }, ...usersList];
  }, [usersList, tString]);

  // Get the current selected user object
  const selectedUserValue = useMemo(() => {
    if (!filterUserId) {
      return userFilterOptions[0]; // "All Users"
    }
    return userFilterOptions.find((u) => u.id === filterUserId) || null;
  }, [filterUserId, userFilterOptions]);

  // Per-user activity summary powering the KPI strip (real aggregates, not a
  // page-only count). Admins don't have a per-user summary, so it's skipped.
  const loadStats = () => {
    if (isAdmin) return;
    authService
      .getActivityStats()
      .then(setStats)
      .catch(() => setStats(null));
  };

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      // Keep the KPI counts in step with the table on every (re)load, including
      // the Refresh button — otherwise they drift stale versus the rows below.
      loadStats();

      let response;
      if (isAdmin) {
        // Admin: fetch all transactions with filters
        const params: any = {
          page: page + 1,
          per_page: rowsPerPage,
          search: searchQuery || undefined,
        };

        if (filterUserId) params.user_id = filterUserId;
        if (filterDateFrom) params.date_from = filterDateFrom;
        if (filterDateTo) params.date_to = filterDateTo;
        if (filterStatus) params.status = filterStatus;

        response = await authService.getAllTransactions(params);

        // Store users list for filter dropdown
        if (response.users) {
          setUsersList(response.users);
        }
      } else {
        // Regular user: fetch only their transactions
        response = await authService.getMyTransactions({
          page: page + 1,
          per_page: rowsPerPage,
          search: searchQuery || undefined,
        });
      }

      const transactions = response.transactions || [];
      setHistory(transactions);
      setTotal(response.total || 0);
    } catch (error: any) {
      console.error('Failed to load processing history:', error);
      setError('Failed to load processing history');
      toast.error('Failed to load processing history');

      // Set empty state on error
      setHistory([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const loadTransactionDetails = async (transactionId: string) => {
    try {
      // Use admin endpoint if user is admin, otherwise use regular endpoint
      const response = isAdmin
        ? await authService.getTransactionDetailsAdmin(transactionId)
        : await authService.getTransactionDetails(transactionId);

      setDetailDialog({
        open: true,
        transaction: response as DetailedTransaction
      });
    } catch (error) {
      toast.error('Failed to load transaction details');
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchQuery, filterUserId, filterDateFrom, filterDateTo, filterStatus, isAdmin]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const clearFilters = () => {
    setFilterUserId('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterStatus('');
    setPage(0);
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const handleViewFile = (item: ProcessingHistoryItem) => {
    setSelectedFileTransaction(item);
    setFileViewerOpen(true);
  };

  const handleCloseFileViewer = () => {
    setFileViewerOpen(false);
    setSelectedFileTransaction(null);
  };

  // Helper function to filter out sensitive data and headers
  const filterSensitiveData = (data: any) => {
    if (!data || typeof data !== 'object') return data;

    const filtered = { ...data };

    // Recursively filter nested objects
    const deepFilter = (obj: any): any => {
      if (!obj || typeof obj !== 'object') return obj;

      if (Array.isArray(obj)) {
        return obj.map(deepFilter);
      }

      const result: any = {};
      Object.keys(obj).forEach(key => {
        if (!key || typeof key !== 'string') return;
        const keyLower = key.toLowerCase();

        // Only remove if it's clearly a header or sensitive auth field
        const shouldRemove = (
          // Headers
          keyLower === 'headers' ||
          keyLower === 'request_headers' ||
          keyLower === 'response_headers' ||
          keyLower.startsWith('x-') ||
          keyLower === 'user-agent' ||
          keyLower === 'content-type' ||
          keyLower === 'accept' ||
          keyLower === 'host' ||
          keyLower === 'cookie' ||
          keyLower === 'referer' ||
          keyLower === 'origin' ||
          // Token and auth related
          keyLower === 'authorization' ||
          keyLower === 'bearer_token' ||
          keyLower === 'access_token' ||
          keyLower === 'auth_token' ||
          keyLower === 'authentication_token' ||
          keyLower === 'refresh_token' ||
          keyLower === 'id_token' ||
          keyLower === 'session_token' ||
          keyLower === 'csrf_token' ||
          keyLower === 'oauth_token' ||
          keyLower === 'jwt' ||
          keyLower === 'api_token' ||
          keyLower === 'client_secret' ||
          keyLower === 'client_token' ||
          keyLower === 'application_token' ||
          keyLower === 'bearer' ||
          keyLower === 'apikey' ||
          keyLower === 'authtoken' ||
          keyLower === 'accesstoken' ||
          keyLower === 'api_key' ||
          keyLower === 'secret' ||
          keyLower === 'password' ||
          // Also check if the key contains token-related substrings
          keyLower.includes('token') ||
          keyLower.includes('secret') ||
          keyLower.includes('password') ||
          keyLower.includes('apikey') ||
          keyLower.includes('api_key')
        );

        if (!shouldRemove) {
          result[key] = deepFilter(obj[key]);
        }
      });
      return result;
    };

    return deepFilter(filtered);
  };

  // Renders an arbitrary data object as a clean, flat Field/Value table.
  const renderDataAsTable = (data: any, title: string) => {
    if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No {title.toLowerCase()} available
          </Typography>
        </Box>
      );
    }

    const filteredData = filterSensitiveData(data);
    const entries = Object.entries(filteredData);

    if (entries.length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No {title.toLowerCase()} to display (all data filtered)
          </Typography>
        </Box>
      );
    }

    // Function to flatten nested objects with dot notation
    const flattenObject = (obj: any, prefix = '', depth = 0): Array<[string, any]> => {
      const result: Array<[string, any]> = [];

      // Prevent infinite recursion
      if (depth > 20) {
        result.push([prefix, JSON.stringify(obj, null, 2)]);
        return result;
      }

      Object.keys(obj).forEach(key => {
        const value = obj[key];
        const newKey = prefix ? `${prefix}.${key}` : key;

        if (value === null || value === undefined) {
          result.push([newKey, value]);
        } else if (Array.isArray(value)) {
          if (value.length === 0) {
            result.push([newKey, '[]']);
          } else if (typeof value[0] === 'object' && value[0] !== null) {
            // Array contains objects - flatten each item
            value.forEach((item, index) => {
              if (typeof item === 'object' && item !== null) {
                result.push(...flattenObject(item, `${newKey}[${index}]`, depth + 1));
              } else {
                result.push([`${newKey}[${index}]`, item]);
              }
            });
          } else {
            // Array of primitives
            result.push([newKey, value.join(', ')]);
          }
        } else if (typeof value === 'object') {
          if (Object.keys(value).length === 0) {
            result.push([newKey, '{}']);
          } else {
            result.push(...flattenObject(value, newKey, depth + 1));
          }
        } else {
          result.push([newKey, value]);
        }
      });

      return result;
    };

    const flattenedEntries = flattenObject(filteredData);

    return (
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ maxHeight: 300, borderRadius: 2 }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600, width: '40%' }}>Field</TableCell>
              <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 600 }}>Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {flattenedEntries.map(([key, value], index) => {
              const displayKey = key.replace(/_/g, ' ').replace(/\./g, ' › ');
              const displayValue = Array.isArray(value)
                ? value.join(', ')
                : typeof value === 'object' && value !== null
                ? JSON.stringify(value)
                : String(value ?? 'N/A');

              return (
                <TableRow
                  key={`${key}-${index}`}
                  sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <TableCell sx={{ fontWeight: 500, textTransform: 'capitalize', verticalAlign: 'top', color: 'text.secondary' }}>
                    {displayKey}
                  </TableCell>
                  <TableCell
                    sx={{
                      wordBreak: 'break-word',
                      fontFamily: displayValue.length > 50 ? 'monospace' : 'inherit',
                    }}
                  >
                    {displayValue}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const headCellSx = {
    bgcolor: 'grey.50',
    fontWeight: 700,
    fontSize: '0.75rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    color: 'text.secondary',
    borderBottom: '1px solid',
    borderColor: 'divider',
    whiteSpace: 'nowrap' as const,
  };

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <PageTitleBar
        title={tString('adminPage.processingHistory.title')}
        subtitle={tString('adminPage.processingHistory.subtitle')}
      />

      {/* KPI summary (per-user aggregates) */}
      {!isAdmin && stats && (
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <StatCard label={tString('adminPage.processingHistory.kpi.totalDocuments')} value={stats.total_documents.toLocaleString()} icon={<Insights />} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label={tString('adminPage.processingHistory.kpi.documentProcessing')} value={stats.document_processing.toLocaleString()} icon={<CloudUpload />} color="#1976D2" />
          </Grid>
          <Grid item xs={6} md={3}>
            <Box
              onClick={() => navigate('/tariff-classifier?tab=history')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/tariff-classifier?tab=history');
                }
              }}
              sx={{ height: '100%', cursor: 'pointer', borderRadius: 3, '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 } }}
            >
              <StatCard label={tString('adminPage.processingHistory.kpi.tariffClassification')} value={stats.tariff_classification.toLocaleString()} icon={<ReceiptLong />} color="#2E7D32" />
            </Box>
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label={tString('adminPage.processingHistory.kpi.emailProcessing')} value={stats.email_processing.toLocaleString()} icon={<Inbox />} color={brand.yellow} />
          </Grid>
        </Grid>
      )}

      {/* Toolbar: search + result count + refresh (+ filter toggle for admins) */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField
            fullWidth
            size="small"
            placeholder={tString('adminPage.processingHistory.filters.search')}
            value={searchQuery}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
            <Chip
              label={tString(
                `adminPage.processingHistory.toolbar.${total === 1 ? 'transactionsOne' : 'transactionsOther'}`,
                { count: total.toLocaleString() }
              )}
              variant="outlined"
              size="small"
              sx={{ fontWeight: 600 }}
            />
            {isAdmin && (
              <Button
                variant={showFilters || hasActiveFilters ? 'contained' : 'outlined'}
                color={hasActiveFilters ? 'primary' : 'inherit'}
                size="small"
                startIcon={showFilters ? <FilterListOff /> : <FilterList />}
                onClick={() => setShowFilters((v) => !v)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {tString('adminPage.processingHistory.toolbar.filters')}{hasActiveFilters ? ' •' : ''}
              </Button>
            )}
            <Tooltip title={tString('adminPage.processingHistory.toolbar.refresh')}>
              <span>
                <IconButton onClick={loadHistory} disabled={loading} color="primary">
                  {loading ? <CircularProgress size={20} /> : <Refresh />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Admin Filters */}
        {isAdmin && (
          <Collapse in={showFilters} timeout="auto" unmountOnExit>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={userFilterOptions}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    return option.id === '' ? option.name : `${option.name} (${option.email})`;
                  }}
                  value={selectedUserValue}
                  onChange={(event, newValue) => {
                    setFilterUserId(newValue?.id || '');
                    setPage(0);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={tString('adminPage.processingHistory.filters.filterByUser')}
                      placeholder={tString('adminPage.processingHistory.filters.searchUsers')}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id || 'all-users'}>
                      {option.id === '' ? <strong>{option.name}</strong> : `${option.name} (${option.email})`}
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return option.id === value.id;
                  }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label={tString('adminPage.processingHistory.filters.dateFrom')}
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => {
                    setFilterDateFrom(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label={tString('adminPage.processingHistory.filters.dateTo')}
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => {
                    setFilterDateTo(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={8} md={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={tString('adminPage.processingHistory.filters.status')}
                  value={filterStatus || ''}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="">
                    <strong>{tString('adminPage.processingHistory.filters.allStatus')}</strong>
                  </MenuItem>
                  <MenuItem value="success">{tString('adminPage.processingHistory.statusValues.success')}</MenuItem>
                  <MenuItem value="failed">{tString('adminPage.processingHistory.statusValues.failed')}</MenuItem>
                  <MenuItem value="processing">{tString('adminPage.processingHistory.statusValues.processing')}</MenuItem>
                  <MenuItem value="pending">{tString('adminPage.processingHistory.statusValues.pending')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={4} md={2}>
                <Button
                  fullWidth
                  variant="text"
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                >
                  {tString('adminPage.processingHistory.filters.clearFilters')}
                </Button>
              </Grid>
            </Grid>
          </Collapse>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading skeleton (first load) */}
      {loading && history.length === 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
          <Stack spacing={1.5}>
            {Array.from({ length: rowsPerPage > 6 ? 6 : rowsPerPage }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={52} />
            ))}
          </Stack>
        </Paper>
      ) : history.length === 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 2, py: 8, px: 3, textAlign: 'center' }}>
          <ReceiptLong sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {tString(
              `adminPage.processingHistory.emptyState.${searchQuery || hasActiveFilters ? 'noMatchTitle' : 'noneTitle'}`
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
            {tString(
              `adminPage.processingHistory.emptyState.${searchQuery || hasActiveFilters ? 'noMatchBody' : 'noneBody'}`
            )}
          </Typography>
          {(searchQuery || hasActiveFilters) && (
            <Button
              variant="outlined"
              size="small"
              sx={{ mt: 2 }}
              onClick={() => {
                setSearchQuery('');
                clearFilters();
              }}
            >
              {tString('adminPage.processingHistory.emptyState.clearSearchFilters')}
            </Button>
          )}
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
            <Table sx={{ '& .MuiTableCell-root': { borderColor: 'divider' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>{tString('adminPage.processingHistory.table.status')}</TableCell>
                  {isAdmin && (
                    <TableCell sx={headCellSx}>{tString('adminPage.processingHistory.table.user')}</TableCell>
                  )}
                  <TableCell sx={headCellSx}>{tString('adminPage.processingHistory.table.file')}</TableCell>
                  <TableCell sx={headCellSx}>{tString('adminPage.processingHistory.table.module')}</TableCell>
                  <TableCell sx={headCellSx}>{tString('adminPage.processingHistory.table.type')}</TableCell>
                  {isAdmin && (
                    <TableCell sx={headCellSx}>{tString('adminPage.processingHistory.table.ipAddress')}</TableCell>
                  )}
                  <TableCell sx={headCellSx}>{tString('adminPage.processingHistory.table.processedAt')}</TableCell>
                  <TableCell align="right" sx={headCellSx}>{tString('adminPage.processingHistory.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((item) => {
                  const statusColor = STATUS_META[item.status]?.color || 'info';
                  return (
                    <TableRow
                      key={item.id}
                      hover
                      onClick={() => loadTransactionDetails(item.id)}
                      sx={{
                        cursor: 'pointer',
                        // Coloured status accent on the leading edge of the row.
                        '& td:first-of-type': {
                          borderLeft: '3px solid',
                          borderLeftColor: `${statusColor}.main`,
                        },
                      }}
                    >
                      <TableCell>
                        <StatusChip status={item.status} tString={tString} />
                      </TableCell>

                      {isAdmin && (
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {(item as any).user_name || 'Unknown User'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {(item as any).user_email || ''}
                          </Typography>
                        </TableCell>
                      )}

                      <TableCell sx={{ maxWidth: 260 }}>
                        <Typography variant="body2" fontWeight={600} noWrap title={item.file_name}>
                          {item.file_name || 'Unknown File'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {formatFileSize(item.file_size)}
                          {item.file_type ? ` • ${item.file_type}` : ''}
                          {item.data_identifier ? ` • ${item.data_identifier}` : ''}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {item.module_name}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <TypeBadge type={item.transaction_type} tString={tString} />
                      </TableCell>

                      {isAdmin && (
                        <TableCell>
                          <Tooltip title={item.user_agent || 'Unknown User Agent'}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Computer sx={{ fontSize: 16, color: 'text.disabled' }} />
                              <Typography variant="body2" noWrap>
                                {item.client_ip || 'Unknown IP'}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>
                      )}

                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <AccessTime sx={{ fontSize: 16, color: 'text.disabled' }} />
                          <Box>
                            <Typography variant="body2" noWrap>
                              {formatDateTime(item.created_at)}
                            </Typography>
                            {item.processing_time && (
                              <Typography variant="caption" color="text.secondary">
                                {item.processing_time}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {(item.file_id || item.file_path) && (
                            <Tooltip title={tString('adminPage.processingHistory.table.fileView')}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewFile(item);
                                }}
                              >
                                <FileIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title={tString('adminPage.processingHistory.toolbar.viewDetails')}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                loadTransactionDetails(item.id);
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      {/* Detailed Transaction Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ReceiptLong color="primary" />
            <span>Transaction details</span>
            {detailDialog.transaction && <StatusChip status={detailDialog.transaction.status} tString={tString} />}
          </Box>
          <IconButton onClick={() => setDetailDialog({ open: false })} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailDialog.transaction && (
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Transaction information
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <DetailRow label={tString('adminPage.processingHistory.expandedView.transactionId')} value={detailDialog.transaction.id} mono />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <DetailRow label={tString('adminPage.processingHistory.expandedView.clientIp')} value={detailDialog.transaction.client_ip} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <DetailRow label="Processing time" value={detailDialog.transaction.processing_time || 'N/A'} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <DetailRow label={tString('adminPage.processingHistory.expandedView.createdAt')} value={formatDateTime(detailDialog.transaction.created_at)} />
                    </Grid>
                    {detailDialog.transaction.completed_at && (
                      <Grid item xs={12} sm={6} md={4}>
                        <DetailRow label={tString('adminPage.processingHistory.expandedView.completedAt')} value={formatDateTime(detailDialog.transaction.completed_at)} />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={4}>
                      <DetailRow label={tString('adminPage.processingHistory.expandedView.userAgent')} value={detailDialog.transaction.user_agent} />
                    </Grid>
                    {detailDialog.transaction.retry_count > 0 && (
                      <Grid item xs={12} sm={6} md={4}>
                        <DetailRow label="Retry count" value={String(detailDialog.transaction.retry_count)} />
                      </Grid>
                    )}
                    {detailDialog.transaction.error_message && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Error message
                        </Typography>
                        <Alert severity="error" sx={{ mt: 0.5 }}>
                          {detailDialog.transaction.error_message}
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              {detailDialog.transaction.transaction_type === 'Module API Post' ? (
                <>
                  <Grid item xs={12} md={6}>
                    <DataPanel icon={<SendIcon fontSize="small" />} title="Posted data (sent to API)" color={theme.palette.primary.main} maxHeight={320}>
                      {renderDataAsTable(detailDialog.transaction.posted_data, 'Posted Data')}
                    </DataPanel>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DataPanel icon={<CheckCircle fontSize="small" />} title="API response (received)" color={theme.palette.success.main} maxHeight={320}>
                      {renderDataAsTable(detailDialog.transaction.api_response, 'API Response')}
                    </DataPanel>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={6}>
                    <DataPanel icon={<FileIcon fontSize="small" />} title="File details" color={theme.palette.info.main} maxHeight={320}>
                      <Stack spacing={1}>
                        <DetailRow label="File name" value={detailDialog.transaction.file?.original_filename || 'Unknown'} />
                        <DetailRow label="File size" value={detailDialog.transaction.file?.file_size ? formatFileSize(detailDialog.transaction.file.file_size) : 'Unknown'} />
                        <DetailRow label="File type" value={detailDialog.transaction.file?.mime_type || 'Unknown'} />
                        <DetailRow label="Upload time" value={detailDialog.transaction.file?.uploaded_at ? formatDateTime(detailDialog.transaction.file.uploaded_at) : 'Unknown'} />
                      </Stack>
                    </DataPanel>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DataPanel icon={<Search fontSize="small" />} title="Data extracted from file" color={theme.palette.warning.main} maxHeight={320}>
                      {renderDataAsTable(detailDialog.transaction.extracted_data, 'Extracted Data')}
                    </DataPanel>
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog({ open: false })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* File Viewer Modal */}
      <FileViewerModal
        open={fileViewerOpen}
        onClose={handleCloseFileViewer}
        transactionId={selectedFileTransaction?.file_id || selectedFileTransaction?.id || ''}
        filename={selectedFileTransaction?.file_name}
        isAdmin={isAdmin}
      />
    </Box>
  );
};

// Status pill shared across the table and both dialogs. Defined at module scope
// (not inside the render body) so it keeps a stable component identity and never
// forces React to remount the rows/panels it lives in.
const StatusChip: React.FC<{
  status: string;
  tString: (key: string, ...p: any[]) => string;
  size?: 'small' | 'medium';
}> = ({ status, tString, size = 'small' }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  // t() returns the key path verbatim on a miss, so an exact match means the
  // status has no translation — fall back to a humanised label.
  const key = `adminPage.processingHistory.statusValues.${status}`;
  const translated = tString(key);
  const label = translated === key ? humanizeStatus(status) : translated;
  return (
    <Chip
      icon={meta.icon}
      label={label}
      color={meta.color as any}
      size={size}
      sx={{ fontWeight: 600, '& .MuiChip-icon': { color: 'inherit' } }}
    />
  );
};

const TypeBadge: React.FC<{ type: string; tString: (key: string, ...p: any[]) => string }> = ({ type, tString }) => {
  const isPost = type === 'Module API Post';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {isPost ? (
        <SendIcon sx={{ fontSize: 18, color: 'success.main' }} />
      ) : (
        <CloudUpload sx={{ fontSize: 18, color: 'primary.main' }} />
      )}
      <Typography variant="body2" fontWeight={500} color={isPost ? 'success.main' : 'primary.main'}>
        {tString(`adminPage.processingHistory.types.${isPost ? 'apiPost' : 'extraction'}`)}
      </Typography>
    </Box>
  );
};

// A titled panel (icon + coloured accent) used inside the expanded row and the
// detail dialog to frame posted data / API response / extracted data.
const DataPanel: React.FC<{
  icon: React.ReactElement;
  title: string;
  color: string;
  maxHeight?: number;
  children: React.ReactNode;
}> = ({ icon, title, color, maxHeight = 220, children }) => (
  <Paper variant="outlined" sx={{ height: '100%', overflow: 'hidden' }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1.25,
        bgcolor: `${color}12`,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ color, display: 'flex' }}>{icon}</Box>
      <Typography variant="subtitle2" fontWeight={600} sx={{ color }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2, maxHeight, overflow: 'auto' }}>{children}</Box>
  </Paper>
);

// Small label/value pair used throughout the expanded row and detail dialog.
const DetailRow: React.FC<{
  label: string;
  value: string;
  mono?: boolean;
  icon?: React.ReactElement;
}> = ({ label, value, mono, icon }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {icon}
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ wordBreak: 'break-word', fontFamily: mono ? 'monospace' : 'inherit', fontSize: mono ? '0.8rem' : undefined }}
    >
      {value}
    </Typography>
  </Box>
);

export default ProcessingHistory;
